/**
 * Deterministic, distinct accent per voice profile.
 *
 * Goal: when a workspace has multiple voices (e.g. "Angad" + "LeanSuite"),
 * each one gets a stable visual identity — a small color dot + a hue used
 * for the voice-name label. The same voice always renders the same color
 * across the whole app (admin kanban, list, public portal, drawer).
 *
 * No setup required — color is derived from the voice_profile_id hash.
 */

// Editorial palette — tuned for both the dark Midnight Library UI and the
// light client portal. Each entry stores a foreground (text on dark bg),
// a portal foreground (text on cream bg), and a swatch (the dot color).
const PALETTE = [
  { swatch: "#C45B38", dark: "#E2876B", light: "#A84C2E", key: "terracotta" }, // burnt orange
  { swatch: "#4A5D4E", dark: "#7E9583", light: "#3A4D40", key: "sage" },        // muted green
  { swatch: "#3D6B95", dark: "#76A4CB", light: "#2F5478", key: "indigo" },      // ink blue
  { swatch: "#B89047", dark: "#D5B271", light: "#8E6E32", key: "brass" },       // antique gold
  { swatch: "#8E4585", dark: "#B574A9", light: "#723869", key: "plum" },        // editorial plum
  { swatch: "#2F7D6E", dark: "#5FA899", light: "#22604F", key: "teal" },        // deep teal
  { swatch: "#A04545", dark: "#C97070", light: "#7E3535", key: "garnet" },      // muted garnet
];

const FALLBACK = { swatch: "#6E6E6A", dark: "#9C9C97", light: "#5A5A57", key: "stone" };

// Simple, stable string hash (djb2 variant).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function voiceColor(voiceId) {
  if (!voiceId) return FALLBACK;
  return PALETTE[hashString(String(voiceId)) % PALETTE.length];
}

/**
 * <VoiceChip /> — small inline pill: colored dot + voice name.
 * Renders nothing if no voice attached.
 *
 * variant="dark"  — for the internal Midnight Library UI
 * variant="light" — for the public client portal
 */
import React from "react";

export function VoiceChip({ voiceId, voiceName, variant = "dark", size = "sm", testid }) {
  if (!voiceName) return null;
  const c = voiceColor(voiceId);
  const fg = c.light;
  const isSm = size === "sm";
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${isSm ? "text-[11px]" : "text-xs"} font-medium`}
      style={{ color: fg }}
      data-testid={testid || `voice-chip-${voiceId || "none"}`}
      title={voiceName}
    >
      <span
        aria-hidden
        style={{
          background: c.swatch,
          width: isSm ? 7 : 9,
          height: isSm ? 7 : 9,
          borderRadius: 999,
          display: "inline-block",
          boxShadow: variant === "light"
            ? "0 0 0 1px rgba(0,0,0,0.06)"
            : "0 0 0 1px rgba(255,255,255,0.04)",
        }}
      />
      <span className="truncate max-w-[120px]">{voiceName}</span>
    </span>
  );
}
